/**
 * Header Component
 * Slim pill-shaped navbar with hamburger menu overlay and search overlay
 * Matches Figma Homepage_V2 navbar design
 */

import { useState, useCallback, useEffect, type ReactNode } from "react";
import type {
  Menu,
  Topbar,
  Pages,
  LocalizedTexts,
  UmbracoLink,
} from "@/types/layout";
import type { LocaleOptions } from "@/api/locale";
import { SearchIcon, UserIcon, CartIcon, MenuIcon } from "@/components/icons";
import { t } from "@/utils/translations";
import { resolveHref } from "@/utils/url";
import {
  buildLoginUrl,
  buildSignoutUrl,
  isAccountDestination,
} from "@/utils/auth";
import { getEnv } from "@/config/env";
import { B2B_USER_TYPE } from "@/types/b2bAccount";
import {
  buildLocalePath,
  getLanguageFlagSrc,
  getFlagEmoji,
} from "@/utils/locale-url";
import { resolveLocaleDisplay } from "@/utils/locale-display";
import { MenuContentCard } from "./MenuContentCard";
import { SearchOverlay } from "./SearchOverlay";
import styles from "./Header.module.css";

/* ─── SVG Icons ─────────────────────────────────────────────────────────────── */

const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/* ─── Props ──────────────────────────────────────────────────────────────────── */

interface HeaderProps {
  menu: Menu;
  topbar: Topbar;
  pages: Pages;
  localizedTexts: LocalizedTexts;
  isUserAuthenticated: boolean;
  /** Member type from the layout payload; B2B members route to the B2B account page. */
  userType?: string | null;
  currentLocale: string;
  cartItemCount?: number;
  currentCountryCode?: string;
  /** Country display names for the menu's country row; same payload the footer uses. */
  localeOptions?: LocaleOptions;
  onCountrySelectorClick?: () => void;
  onCartToggle?: () => void;
  isCartDrawerOpen?: boolean;
  cartDrawer?: ReactNode;
  /**
   * A search another app asked for on arrival (WeHunt Web's top bar submits
   * to `/go/shop?q=`). A new value opens the search overlay with the term
   * typed in; see Layout, which reads it off the URL.
   */
  searchEntry?: { query: string } | null;
}

/* ─── Component ──────────────────────────────────────────────────────────────── */

export function Header({
  menu,
  topbar,
  pages,
  localizedTexts,
  isUserAuthenticated,
  userType,
  currentLocale,
  cartItemCount = 0,
  currentCountryCode = "SE",
  localeOptions,
  onCountrySelectorClick,
  onCartToggle,
  isCartDrawerOpen,
  cartDrawer,
  searchEntry = null,
}: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // A search handed over by another app opens the overlay pre-filled. Keyed
  // on the request object, not its text, so the same term asked twice in a
  // row still reopens it.
  useEffect(() => {
    if (!searchEntry) return;
    setIsMenuOpen(false);
    setIsSearchOpen(true);
  }, [searchEntry]);

  const toggleMenu = useCallback(() => {
    setIsSearchOpen(false);
    setIsMenuOpen((prev) => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const openSearch = useCallback(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
  }, []);

  const closeAll = useCallback(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
  }, []);

  // Lock body scroll when any overlay is open
  useEffect(() => {
    if (isMenuOpen || isSearchOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen, isSearchOpen]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (isSearchOpen) closeSearch();
        else if (isMenuOpen) closeMenu();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMenuOpen, isSearchOpen, closeMenu, closeSearch]);

  const cartText = t(localizedTexts, "Tracker.Generic.Cart", "Cart");
  const searchText = t(localizedTexts, "Tracker.Generic.Search", "Search");
  const menuText = t(localizedTexts, "Tracker.Generic.Menu", "Menu");
  const closeText = t(localizedTexts, "Tracker.Generic.Close", "Close");
  const localeSelectorText = t(
    localizedTexts,
    "Tracker.Generic.SelectCountryAndLanguage",
    "Select country and language"
  );

  // Authenticated B2B members get their own account portal; everyone else
  // (B2C members + guests heading to login) uses the B2C my-account page.
  const isB2bMember = isUserAuthenticated && userType === B2B_USER_TYPE;
  const accountUrl = resolveHref(
    isB2bMember ? pages.b2bMyAccountPage : pages.b2cMyAccountPage,
    currentLocale
  );
  // No returnPath for a guest: the backend already sends a WeHunt member to
  // the web app's overview by default when login carries none, and passing
  // the account page here would override that default and pin them there.
  const loginHref = isUserAuthenticated ? accountUrl : buildLoginUrl();

  // Compact login wording (fi-FI "Kirjaudu" rather than "Kirjaudu sisään",
  // which overflows a narrow bar) — shared by the pill-bar guest label and
  // the menu's Log in action.
  const loginLabel = t(
    localizedTexts,
    "Tracker.Generic.Login.Short",
    t(localizedTexts, "Tracker.Generic.Login", "Login")
  );

  // The account control carries a visible label on mobile, where it is the only
  // thing in the pill that names itself. Members get "My account" and go
  // straight to the dashboard.
  const accountLabel = isUserAuthenticated
    ? t(localizedTexts, "Tracker.Generic.MyAccount", "My Account")
    : loginLabel;

  // The menu's top action: Log in for a guest, the B2B portal for a B2B
  // member. A B2C member gets no top action — their Sign out is the last row
  // of the menu's Account section (as in WeHunt Web), which also carries the
  // way to every account tab. B2B members sign out with a POST from their own
  // account page and are never on sso, so their row keeps today's link.
  const menuAccountAction = !isUserAuthenticated
    ? { href: loginHref, label: loginLabel }
    : isB2bMember
      ? { href: accountUrl, label: accountLabel }
      : undefined;

  const crossApp = {
    webAppUrl: getEnv().webAppUrl,
    accountPageHref: isUserAuthenticated ? accountUrl : null,
    isB2b: isB2bMember,
    signOutHref: isUserAuthenticated && !isB2bMember ? buildSignoutUrl() : null,
  };

  // The topbar CTA is a free-form CMS slot, but editors have pointed it at the
  // login page — where it renders as a second account control whose label comes
  // from the CMS and so stays "Login" after sign-in, contradicting the control
  // above. Drop it when it duplicates the account destination; a CTA that leads
  // anywhere else is untouched.
  const ctaLink = topbar.ctaButtonLink;
  const ctaHref = ctaLink ? resolveHref(ctaLink, currentLocale) : null;
  const accountDestinations = [
    pages.b2cMyAccountPage,
    pages.b2bMyAccountPage,
    pages.b2cLoginPage,
    pages.b2bLoginPage,
  ]
    .filter((link): link is UmbracoLink => Boolean(link))
    .map((link) => resolveHref(link, currentLocale));
  const showCtaButton =
    ctaLink !== null &&
    ctaLink !== undefined &&
    !isAccountDestination(ctaHref, accountDestinations);

  const localeDisplay = resolveLocaleDisplay({
    currentLocale,
    currentCountry: currentCountryCode,
    localeOptions,
    fallbackLabel: t(localizedTexts, "Tracker.Generic.Footer.Global", "Global"),
  });

  // Opening the locale selector from inside the menu has to close the menu
  // first: the menu panel sits above the page and would cover the modal.
  const openLocaleSelectorFromMenu = useCallback(() => {
    setIsMenuOpen(false);
    onCountrySelectorClick?.();
  }, [onCountrySelectorClick]);

  const cartAriaLabel =
    cartItemCount > 0
      ? `${cartText}, ${cartItemCount} ${t(localizedTexts, "Tracker.Generic.Items", "items")}`
      : cartText;

  return (
    <>
      <header className={styles.header} role="banner">
        {/* Pill wrapper anchors the cart drawer to the pill bar's right edge */}
        <div className={styles.pillWrapper}>
          {/* Pill Bar (visible when menu is closed) */}
          {!isMenuOpen && (
            <div className={styles.pillBar}>
              <div className={styles.pillLeft}>
                <button
                  className={styles.menuToggle}
                  onClick={toggleMenu}
                  aria-label={menuText}
                  aria-expanded={isMenuOpen}
                  type="button"
                >
                  <MenuIcon />
                </button>
                <a
                  href={buildLocalePath(currentLocale, "/")}
                  className={styles.logoLink}
                  aria-label={t(
                    localizedTexts,
                    "Tracker.Generic.Home",
                    "WeHunt - Home page"
                  )}
                >
                  <img
                    src="/images/wehunt-logo-darkgreen.png"
                    alt="WeHunt"
                    className={styles.logoImage}
                  />
                </a>
              </div>

              <div className={styles.pillRight}>
                {/* Hidden below 360px, where the pill has no room for it and
                    the wordmark would shrink instead; search stays reachable
                    from the menu's own search icon. */}
                <button
                  type="button"
                  className={`${styles.iconButton} ${styles.searchButton}`}
                  aria-label={searchText}
                  onClick={openSearch}
                >
                  <SearchIcon />
                </button>
                {/* A labelled pill at every width, so the one control that
                    knows the signed-in state is also the one that names it.
                    CSS `order` puts it at the right end of the bar. */}
                <a
                  href={loginHref}
                  className={`${styles.iconButton} ${styles.accountLink}`}
                  aria-label={accountLabel}
                >
                  <UserIcon className={styles.accountIcon} />
                  <span className={styles.accountLabel}>{accountLabel}</span>
                </a>
                <button
                  type="button"
                  className={styles.iconButton}
                  aria-label={cartAriaLabel}
                  aria-expanded={isCartDrawerOpen}
                  onClick={onCartToggle}
                >
                  <CartIcon />
                  {cartItemCount > 0 && (
                    <span className={styles.cartBadge} aria-hidden="true">
                      {cartItemCount}
                    </span>
                  )}
                </button>

                {/* Country selector */}
                {/* Hidden below 768px — the menu's country row takes over there,
                    freeing the width the labelled login pill needs. */}
                <button
                  className={styles.countrySelector}
                  onClick={onCountrySelectorClick}
                  aria-label={localeSelectorText}
                  type="button"
                >
                  {(() => {
                    const langCode =
                      currentLocale.split("-")[0] ?? currentLocale;
                    const langFlag = getLanguageFlagSrc(langCode);
                    return langFlag ? (
                      <img
                        src={langFlag}
                        alt=""
                        aria-hidden="true"
                        width={20}
                        height={20}
                        style={{
                          display: "block",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <span aria-hidden="true">
                        {getFlagEmoji(currentCountryCode ?? "SE")}
                      </span>
                    );
                  })()}
                </button>

                {showCtaButton && ctaLink && ctaHref && (
                  <a
                    href={ctaHref}
                    className={styles.ctaButton}
                    target={ctaLink.target ?? undefined}
                  >
                    {ctaLink.name}
                  </a>
                )}
              </div>
            </div>
          )}
          {cartDrawer}
        </div>
      </header>

      {/* Shared overlay backdrop (menu or search) */}
      <div
        className={`${styles.overlay} ${isMenuOpen || isSearchOpen ? styles.overlayOpen : ""}`}
        onClick={closeAll}
        aria-hidden="true"
      />

      {/* Menu Panel */}
      <div
        className={`${styles.menuPanel} ${isMenuOpen ? styles.menuPanelOpen : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Pill bar inside menu (with close icon) */}
        <div className={styles.menuPillBar}>
          <div className={styles.pillLeft}>
            <button
              className={styles.menuToggle}
              onClick={closeMenu}
              aria-label={closeText}
              type="button"
            >
              <CloseIcon />
            </button>
            <a
              href={buildLocalePath(currentLocale, "/")}
              className={styles.logoLink}
              aria-label={t(
                localizedTexts,
                "Tracker.Generic.Home",
                "WeHunt - Home page"
              )}
              onClick={closeMenu}
            >
              <img
                src="/images/wehunt-logo-darkgreen.png"
                alt="WeHunt"
                className={styles.logoImage}
              />
            </a>
          </div>

          <div className={styles.pillRight}>
            <button
              type="button"
              className={styles.iconButton}
              aria-label={searchText}
              onClick={openSearch}
            >
              <SearchIcon />
            </button>
            {/* No account icon here: the card below opens with a labelled
                account row, and two controls for one action reads as two. */}
            <button
              type="button"
              className={styles.iconButton}
              aria-label={cartAriaLabel}
              onClick={() => {
                closeMenu();
                onCartToggle?.();
              }}
            >
              <CartIcon />
              {cartItemCount > 0 && (
                <span className={styles.cartBadge} aria-hidden="true">
                  {cartItemCount}
                </span>
              )}
            </button>
            {showCtaButton && ctaLink && ctaHref && (
              <a
                href={ctaHref}
                className={styles.ctaButton}
                target={ctaLink.target ?? undefined}
                onClick={closeMenu}
              >
                {ctaLink.name}
              </a>
            )}
          </div>
        </div>

        {/* Menu Content */}
        <MenuContentCard
          menu={menu}
          topbar={topbar}
          currentLocale={currentLocale}
          localizedTexts={localizedTexts}
          onLinkClick={closeMenu}
          showImage
          account={menuAccountAction}
          crossApp={crossApp}
          locale={{
            label: localeDisplay.label,
            flagSrc: localeDisplay.flagSrc,
            ariaLabel: localeSelectorText,
            onSelect: openLocaleSelectorFromMenu,
          }}
        />
      </div>

      {/* Search Overlay */}
      <SearchOverlay
        isOpen={isSearchOpen}
        onClose={closeSearch}
        menu={menu}
        topbar={topbar}
        currentLocale={currentLocale}
        localizedTexts={localizedTexts}
        initialQuery={searchEntry?.query}
      />
    </>
  );
}

export default Header;
